import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logSecurityEvent } from '../utils/security';
import { validateEmail, validatePassword } from '../utils/validation';
import type { User } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const checkAuthState = useCallback(async () => {
    if (!supabase) {
      console.warn('Supabase not configured - authentication disabled');
      setLoading(false);
      return;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      setUser(session?.user ?? null);
      
      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed:', event);
          setUser(session?.user ?? null);
          setLoading(false);
          
          // Create user record in our users table if it doesn't exist
          if (session?.user && event === 'SIGNED_IN') {
            try {
              const { error: upsertError } = await supabase
                .from('users')
                .upsert({
                  id: session.user.id,
                  email: session.user.email || '',
                  updated_at: new Date().toISOString()
                }, {
                  onConflict: 'id'
                });
              
              if (upsertError) {
                console.warn('Failed to create user record:', upsertError);
              }
            } catch (err) {
              console.warn('Error creating user record:', err);
            }
          }
        }
      );

      // Clean up subscription on unmount
      return () => subscription.unsubscribe();
      
    } catch (err) {
      console.error('Error checking auth state:', err);
      setError('Failed to check authentication state');
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      setError('Authentication service not available');
      throw new Error('Authentication service not available');
    }

    // Enhanced input validation
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      throw new Error('Please enter a valid email address');
    }
    
    if (!password || password.length < 1) {
      setError('Password is required');
      throw new Error('Password is required');
    }

    setLoading(true);
    setError(undefined);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });

      if (error) throw error;
      
      setUser(data.user);
      
      logSecurityEvent({
        type: 'auth_attempt',
        user: email,
        success: true
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      
      logSecurityEvent({
        type: 'auth_attempt',
        user: email,
        details: errorMessage,
        success: false
      });
      
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    if (!supabase) {
      setError('Authentication service not available');
      throw new Error('Authentication service not available');
    }

    // Enhanced input validation for sign up
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      throw new Error('Please enter a valid email address');
    }

    if (!validatePassword(password)) {
      setError('Password must be 8-128 characters with uppercase, lowercase, and numbers/special characters');
      throw new Error('Password must be 8-128 characters with uppercase, lowercase, and numbers/special characters');
    }
    
    setLoading(true);
    setError(undefined);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      
      setUser(data.user);
      
      logSecurityEvent({
        type: 'auth_attempt',
        user: email,
        success: true
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Account creation failed';
      setError(errorMessage);
      
      logSecurityEvent({
        type: 'auth_attempt',
        user: email,
        details: errorMessage,
        success: false
      });
      
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (!supabase) return;

    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      
      logSecurityEvent({
        type: 'auth_attempt',
        success: true
      });
      
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out');
    }
  };

  return {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    checkAuthState
  };
};